import { useState, useCallback, useEffect } from "react";
import { Plus, Trash2, Edit, ChevronDown, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { NumericInput } from "../ui/numeric-input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { SearchableSelect } from "../ui/searchable-select";
import { useBillingCategories } from "../../hooks/useBillingCategories";

/**
 * Materials Section - handles material line items
 * Includes table display, add/edit dialog, and category grouping
 */
export const MaterialsSection = ({
  materialItems,
  materials, // from settings
  canManageJobs,
  formatCurrency,
  onAddItem,
  onEditItem,
  onDeleteItem,
  materialSubtotal,
  preSelectItem,
  onPreSelectHandled,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  const [form, setForm] = useState({
    category: "",
    item_id: "",
    quantity: "1",
    unit_price: "",
    labor_amount: "",
    mortar_amount: "",
    is_override: false,
    description: "",
    billing_category_override: "",
  });
  const billing = useBillingCategories();
  const requiresBilling = !!form.item_id && billing.perJobMaterials?.has(form.item_id);

  // Get unique categories from settings
  const availableCategories = [...new Set(materials.map(m => m.category))].filter(Boolean);
  
  // Filter materials by selected category
  const filteredMaterials = form.category 
    ? materials.filter(m => m.category === form.category)
    : [];

  // Group material items by category for display
  const groupedItems = materialItems.reduce((acc, item) => {
    const cat = item.category || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const resetForm = useCallback(() => {
    setForm({
      category: "",
      item_id: "",
      quantity: "1",
      unit_price: "",
      labor_amount: "",
      mortar_amount: "",
      is_override: false,
      description: "",
    });
    setEditingItem(null);
  }, []);

  const openDialog = useCallback((item = null) => {
    if (item) {
      setEditingItem(item);
      setForm({
        category: item.category,
        item_id: item.item_id,
        quantity: item.quantity.toString(),
        unit_price: typeof item.unit_price === 'number' ? item.unit_price.toFixed(2) : "",
        labor_amount: typeof item.labor_per_unit === 'number' && item.labor_per_unit > 0 ? item.labor_per_unit.toFixed(2) : "",
        mortar_amount: typeof item.mortar_per_unit === 'number' && item.mortar_per_unit > 0 ? item.mortar_per_unit.toFixed(2) : "",
        is_override: item.is_override,
        description: item.description || "",
        billing_category_override: item.billing_category_override || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  }, [resetForm]);

  // Auto-open dialog when preSelectItem is set from universal search
  useEffect(() => {
    if (preSelectItem) {
      const mat = materials.find(m => m.id === preSelectItem.id);
      if (mat) {
        setEditingItem(null);
        setForm({
          category: mat.category || "",
          item_id: mat.id,
          quantity: "1",
          unit_price: mat.user_defined_price ? "" : mat.unit_price.toString(),
          labor_amount: "",
          mortar_amount: "",
          is_override: mat.user_defined_price || false,
          description: "",
        });
        setDialogOpen(true);
      }
      if (onPreSelectHandled) onPreSelectHandled();
    }
  }, [preSelectItem, materials, onPreSelectHandled]);

  const handleItemSelect = useCallback((itemId) => {
    const selectedItem = materials.find(m => m.id === itemId);
    if (selectedItem) {
      setForm(f => ({
        ...f,
        item_id: itemId,
        unit_price: selectedItem.user_defined_price ? "" : selectedItem.unit_price.toString(),
        is_override: selectedItem.user_defined_price,
      }));
    }
  }, [materials]);

  const handleSubmit = useCallback(() => {
    const selectedMaterial = materials.find(m => m.id === form.item_id);
    if (!selectedMaterial) return;

    const quantity = parseFloat(form.quantity) || 1;
    const unitPrice = parseFloat(form.unit_price) || selectedMaterial.unit_price || 0;
    const laborPerUnit = parseFloat(form.labor_amount) || 0;
    const mortarPerUnit = parseFloat(form.mortar_amount) || 0;

    const newItem = {
      id: editingItem?.id || crypto.randomUUID(),
      category: form.category,
      item_id: form.item_id,
      item_name: selectedMaterial.name,
      quantity,
      unit: selectedMaterial.unit || "each",
      unit_price: unitPrice,
      total: quantity * unitPrice,
      labor_per_unit: laborPerUnit,
      labor_total: laborPerUnit * quantity,
      mortar_per_unit: mortarPerUnit,
      mortar_total: mortarPerUnit * quantity,
      is_override: form.is_override || unitPrice !== selectedMaterial.unit_price,
      description: form.description,
      billing_category_override: form.billing_category_override || null,
    };

    if (editingItem) {
      onEditItem(newItem);
    } else {
      onAddItem(newItem);
    }

    setDialogOpen(false);
    resetForm();
  }, [form, materials, editingItem, onAddItem, onEditItem, resetForm]);

  const toggleCategory = useCallback((category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-2xl flex items-center gap-3 text-slate-700">
            <span>Materials</span>
            <span className="text-muted-foreground font-normal">&ndash;</span>
            <span className="text-xl font-normal text-slate-500">Subtotal</span>
            <span className="font-mono font-bold text-2xl text-slate-700" data-testid="materials-subtotal">{formatCurrency(materialSubtotal)}</span>
          </CardTitle>
          {canManageJobs && (
            <Button size="lg" className="text-lg px-6" onClick={() => openDialog()} data-testid="add-material-item-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Item
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {materialItems.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <p>No materials added yet</p>
              <p className="text-sm mt-1">Click &quot;Add Item&quot; to add materials</p>
            </div>
          ) : (
            <div>
              {Object.entries(groupedItems).map(([category, items]) => (
                <Collapsible
                  key={category}
                  open={expandedCategories[category] !== false}
                  onOpenChange={() => toggleCategory(category)}
                >
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 hover:bg-slate-600 border-b bg-slate-700 text-white">
                    <div className="flex items-center gap-2">
                      {expandedCategories[category] === false ? (
                        <ChevronRight className="w-4 h-4 text-white" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-white" />
                      )}
                      <span className="font-semibold text-[0.95rem]">{category}</span>
                      <span className="text-sm text-slate-300">({items.length})</span>
                    </div>
                    <span className="font-mono font-medium text-white">
                      {formatCurrency(items.reduce((sum, i) => sum + (i.total || 0) + (i.mortar_total || 0), 0))}
                    </span>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <Table className="table-fixed">
                      <TableHeader>
                        <TableRow className="bg-slate-100 border-b-2 border-slate-200">
                          <TableHead className="h-8 py-1 pl-10 text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Item</TableHead>
                          <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Qty</TableHead>
                          <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Unit Price</TableHead>
                          <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Material</TableHead>
                          <TableHead className="h-8 py-1 w-32 text-right text-xs uppercase tracking-wider text-slate-700" style={{fontSize: '0.82rem'}}>Mortar</TableHead>
                          {canManageJobs && <TableHead className="h-8 py-1 w-20"></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item, idx) => (
                          <TableRow key={item.id} className="border-b border-slate-100">
                            <TableCell className="pl-10 py-3">
                              <div>
                                <span className="font-medium">{item.item_name}</span>
                                {item.is_override && <span className="ml-2 text-xs text-amber-600">(Override)</span>}
                                {item.billing_category_override && <span className="ml-2 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid={`bid-material-billing-override-${item.id}`}>Billing: {item.billing_category_override}</span>}
                                {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell className="text-right font-mono font-medium">{formatCurrency(item.total)}</TableCell>
                            <TableCell className="text-right font-mono">{item.mortar_total > 0 ? formatCurrency(item.mortar_total) : "-"}</TableCell>
                            {canManageJobs && (
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDialog(item)} data-testid={`edit-bid-material-${item.id}`}>
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDeleteItem(item.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Material Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit" : "Add"} Material Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="form-group">
              <Label>Category *</Label>
              <Select 
                value={form.category} 
                onValueChange={(v) => setForm(f => ({...f, category: v, item_id: "", unit_price: ""}))}
              >
                <SelectTrigger data-testid="item-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {[...availableCategories].sort((a, b) => a.localeCompare(b)).map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <Label>Item *</Label>
              <Select 
                value={form.item_id} 
                onValueChange={handleItemSelect}
                disabled={!form.category}
              >
                <SelectTrigger data-testid="item-select">
                  <SelectValue placeholder={form.category ? "Select item" : "Select category first"} />
                </SelectTrigger>
                <SelectContent>
                  {[...filteredMaterials].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} {item.user_defined_price ? "(Variable)" : `(${formatCurrency(item.unit_price)}/${item.unit})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.item_id && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <Label>Quantity *</Label>
                    <NumericInput 
                      decimals={2}
                      allowNegative={true}
                      value={form.quantity} 
                      onChange={(e) => setForm(f => ({...f, quantity: e.target.value}))} 
                      data-testid="item-quantity-input" 
                    />
                  </div>
                  <div className="form-group">
                    <Label>Unit Price ($) {form.is_override && <span className="text-amber-600">(Override)</span>}</Label>
                    <NumericInput 
                      decimals={2}
                      allowNegative={true}
                      value={form.unit_price} 
                      onChange={(e) => setForm(f => ({...f, unit_price: e.target.value, is_override: true}))} 
                      data-testid="item-price-input" 
                    />
                  </div>
                </div>
                {form.quantity && form.unit_price && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-blue-800 font-medium">Material Total:</span>
                      <span className="text-lg font-bold text-blue-700">
                        {formatCurrency((parseFloat(form.quantity) || 1) * (parseFloat(form.unit_price) || 0))}
                      </span>
                    </div>
                  </div>
                )}
                <hr className="my-2" />
                <p className="text-sm font-medium">Additional costs for this material (optional)</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <Label>Labor Cost per Unit ($)</Label>
                    <NumericInput 
                      decimals={2}
                      allowNegative={true}
                      value={form.labor_amount} 
                      onChange={(e) => setForm(f => ({...f, labor_amount: e.target.value}))} 
                      placeholder="0.00"
                      data-testid="material-labor-amount-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">→ Labor subtotal</p>
                  </div>
                  <div className="form-group">
                    <Label>Mortar Cost per Unit ($)</Label>
                    <NumericInput 
                      decimals={2}
                      min={0}
                      value={form.mortar_amount} 
                      onChange={(e) => setForm(f => ({...f, mortar_amount: e.target.value}))} 
                      placeholder="0.00"
                      data-testid="material-mortar-amount-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">→ Materials subtotal</p>
                  </div>
                </div>
                {(form.labor_amount || form.mortar_amount) && form.quantity && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-1">
                    {form.labor_amount && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-800">Total Labor:</span>
                        <span className="font-bold text-green-700">
                          {formatCurrency((parseFloat(form.labor_amount) || 0) * (parseFloat(form.quantity) || 1))} → Labor
                        </span>
                      </div>
                    )}
                    {form.mortar_amount && (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-800">Total Mortar:</span>
                        <span className="font-bold text-green-700">
                          {formatCurrency((parseFloat(form.mortar_amount) || 0) * (parseFloat(form.quantity) || 1))} → Materials
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className={`form-group ${requiresBilling && !form.billing_category_override ? "rounded-md border border-amber-300 bg-amber-50 p-2" : ""}`}>
                  <Label>{requiresBilling ? "Billing Category (required for this item)" : "Billing Category Override"}</Label>
                  <SearchableSelect
                    value={form.billing_category_override}
                    onValueChange={(v) => setForm(f => ({ ...f, billing_category_override: v || "" }))}
                    options={billing.categories.map((c) => ({ value: c, label: c }))}
                    placeholder={requiresBilling ? "Choose where this line bills on the SOV..." : billing.materials[form.item_id] ? `Default: ${billing.materials[form.item_id]} (from material)` : "Use material's billing category"}
                    searchPlaceholder="Search billing categories..."
                    data-testid="bid-material-billing-override-select"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{requiresBilling ? "This material is set to pick its billing category per job — pick one to continue." : "Only for this line on this bid sheet — changes where it lands on the Schedule of Values."}</p>
                </div>
                <div className="form-group">
                  <Label>Description / Notes</Label>
                  <Textarea 
                    value={form.description} 
                    onChange={(e) => setForm(f => ({...f, description: e.target.value}))} 
                    rows={2} 
                  />
                </div>
              </>
            )}
            <Button 
              onClick={handleSubmit} 
              className="w-full" 
              disabled={!form.item_id || !form.quantity || (requiresBilling && !form.billing_category_override)}
              data-testid="add-item-submit-btn"
            >
              {editingItem ? "Save Changes" : "Add Item"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MaterialsSection;
